// src/components/blog/components/TableOfContentsList.tsx

import type { ReactNode, Ref } from 'react'
// Import `cn` from the server-safe design module, NOT the `~/lib/utils` barrel:
// that barrel re-exports `./events` (`'use client'`), which would pull a client
// module into this SSR-safe base's graph and risk a spurious RSC boundary
// (this base is rendered by the server `TableOfContentsServer`). #522 `r3525590053`.
import { cn } from '~/lib/utils/design'
import type { TOCItem } from '~/lib/blog/types/blog.types'

/** Props for {@link TableOfContentsList}. */
export interface TableOfContentsListProps {
  /** Heading items to render. */
  items: TOCItem[]
  /** Id of the currently-active heading (interactive variant); omit for the static server variant. */
  activeId?: string
  /** Extra classes for the root `<nav>` (e.g. `sticky top-24` or `relative`). */
  className?: string
  /** Heading label (default "Table of Contents"). */
  label?: string
  /** Header icon; defaults to a small indigo bullet. */
  icon?: ReactNode
  /** Smooth-scroll click handler (interactive variant); omit for plain anchors. */
  onItemClick?: (event: React.MouseEvent, id: string) => void
  /** Ref for the scrollable `<ul>` (interactive variant needs it for auto-scroll + indicators). */
  listRef?: Ref<HTMLUListElement>
  /** Extra classes for the `<ul>` (e.g. the interactive variant's max-height + overflow). */
  listClassName?: string
  /** Extra content inside the `<nav>` (e.g. the interactive variant's scroll indicators). */
  children?: ReactNode
}

/**
 * Single source of truth for the blog table-of-contents markup — the card
 * chrome, header, and the indented anchor list with per-heading dot + active
 * state. Both `TableOfContentsServer` (static, no-JS) and
 * `AnimatedTableOfContents` (interactive scroll-spy) render through this.
 *
 * Intentionally SSR-safe (no `'use client'`, no `motion`) so the static server
 * fallback stays JS-free and its markup matches what the interactive variant
 * hydrates. Interactivity (scroll-spy `activeId`, smooth-scroll `onItemClick`,
 * scroll-indicator `children`) is injected by the client caller via props.
 *
 * @param props - {@link TableOfContentsListProps}.
 */
export function TableOfContentsList({
  items,
  activeId,
  className,
  label = 'Table of Contents',
  icon,
  onItemClick,
  listRef,
  listClassName,
  children,
}: TableOfContentsListProps): JSX.Element {
  return (
    <nav
      aria-label={label}
      className={cn(
        'bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm p-4',
        className,
      )}
    >
      <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
        {icon ?? <span className="mr-2 h-2 w-2 bg-indigo-500 rounded-full" aria-hidden="true" />}
        {label}
      </h2>

      <ul ref={listRef} className={cn('space-y-1 text-sm', listClassName)}>
        {items.length > 0 ? (
          items.map((item) => {
            const isActive = activeId === item.id
            return (
              <li
                key={item.id}
                data-id={item.id}
                style={{ paddingLeft: `${Math.max(0, item.level - 2) * 0.75}rem` }}
              >
                <a
                  href={`#${item.id}`}
                  onClick={onItemClick ? (event) => onItemClick(event, item.id) : undefined}
                  className={cn(
                    'group flex items-start py-1.5 px-3 rounded-md transition-colors duration-200',
                    isActive
                      ? 'bg-indigo-50/70 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30 hover:text-gray-900 dark:hover:text-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'mr-2 h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 transition-colors',
                      isActive
                        ? 'bg-indigo-600 dark:bg-indigo-400'
                        : 'bg-gray-300 dark:bg-gray-600 group-hover:bg-indigo-500 dark:group-hover:bg-indigo-400',
                    )}
                    aria-hidden="true"
                  />
                  <span className="leading-snug">{item.title}</span>
                </a>
              </li>
            )
          })
        ) : (
          <li className="text-gray-500 dark:text-gray-400 italic py-2 px-2">
            No headings found in this article
          </li>
        )}
      </ul>

      {children}
    </nav>
  )
}
